import React from 'react';
import { X, Check, Info } from 'lucide-react';
import { Card } from './ui/card';

export default function PlanComparisonModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="bg-gray-900 border border-purple-500/30 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Compare Our Plans</h2>
            <p className="text-purple-100">Find the perfect plan for your restaurant</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          
          {/* Scheduling Comparison */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Info className="w-6 h-6 text-purple-400" />
              Post Scheduling vs Advanced Scheduling
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pro Scheduling */}
              <Card className="bg-gray-800/50 border border-purple-500/30 p-6">
                <div className="text-center mb-4">
                  <div className="inline-block bg-purple-600/20 px-4 py-2 rounded-full mb-2">
                    <span className="text-purple-300 font-bold">PRO PLAN - $139</span>
                  </div>
                  <h4 className="text-xl font-bold text-white">3 Advanced Scheduling</h4>
                  <p className="text-gray-400 text-sm">Advanced scheduling features for posts</p>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Schedule single posts with date/time picker</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Automatic posting to connected accounts</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>View upcoming scheduled posts (list view)</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Edit/delete scheduled posts</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Email notifications when posts are scheduled/published</span>
                  </li>
                </ul>

                <div className="mt-6 p-4 bg-purple-600/10 rounded-lg border border-purple-500/30">
                  <p className="text-sm text-purple-300 font-semibold mb-1">Best For:</p>
                  <p className="text-gray-400 text-sm">Single location restaurants posting 5-15 times per week</p>
                </div>
              </Card>

              {/* Enterprise Advanced Scheduling */}
              <Card className="bg-gray-800/50 border border-pink-500/30 p-6">
                <div className="text-center mb-4">
                  <div className="inline-block bg-pink-600/20 px-4 py-2 rounded-full mb-2">
                    <span className="text-pink-300 font-bold">ENTERPRISE - $379</span>
                  </div>
                  <h4 className="text-xl font-bold text-white">Advanced Scheduling</h4>
                  <p className="text-gray-400 text-sm">Everything in Pro + Premium automation</p>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span className="font-semibold">All Pro features included</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Bulk scheduling (upload 50+ posts at once)</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Recurring posts (daily specials, weekly promos)</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>AI-powered optimal posting times</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Visual content calendar (drag & drop)</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Multi-location support with separate calendars</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Team collaboration & approval workflows</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Priority queue for rush posts</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Advanced analytics & performance tracking</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                    <span>Social media page handling by dedicated manager</span>
                  </li>
                </ul>

                <div className="mt-6 p-4 bg-pink-600/10 rounded-lg border border-pink-500/30">
                  <p className="text-sm text-pink-300 font-semibold mb-1">Best For:</p>
                  <p className="text-gray-400 text-sm">Multi-location chains, marketing teams posting 20+ times per week</p>
                </div>
              </Card>
            </div>
          </div>

          {/* Complete Feature Comparison Table */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">Complete Feature Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-400 font-semibold">Feature</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-semibold">Free</th>
                    <th className="text-center py-3 px-4 text-purple-400 font-semibold">Basic</th>
                    <th className="text-center py-3 px-4 text-purple-400 font-semibold">Pro</th>
                    <th className="text-center py-3 px-4 text-pink-400 font-semibold">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">AI Image Generation</td>
                    <td className="py-3 px-4 text-center">5</td>
                    <td className="py-3 px-4 text-center">30/month</td>
                    <td className="py-3 px-4 text-center">50/month</td>
                    <td className="py-3 px-4 text-center">100/month</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">Post Creations</td>
                    <td className="py-3 px-4 text-center">5</td>
                    <td className="py-3 px-4 text-center">15/month</td>
                    <td className="py-3 px-4 text-center">30/month</td>
                    <td className="py-3 px-4 text-center">80/month</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">AI Video Content</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">10/month</td>
                    <td className="py-3 px-4 text-center">30/month</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">Social Media Accounts</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">1</td>
                    <td className="py-3 px-4 text-center">1</td>
                    <td className="py-3 px-4 text-center">3</td>
                  </tr>
                  <tr className="border-b border-gray-800 bg-purple-600/10">
                    <td className="py-3 px-4 font-semibold">Advanced Scheduling</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">3</td>
                    <td className="py-3 px-4 text-center">✅</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">Bulk Scheduling</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">✅</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">Recurring Posts</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">✅</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">AI Smart Timing</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">✅</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">Team Collaboration</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">✅</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4">Page Handling</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">-</td>
                    <td className="py-3 px-4 text-center">✅ Dedicated Manager</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Use Cases */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">Which Plan Is Right For Me?</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="bg-gray-800/50 border border-purple-500/30 p-6">
                <h4 className="text-lg font-bold text-white mb-3">Choose Basic ($99) If:</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Small restaurants and food trucks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Post 10-15 times/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Getting started with AI</span>
                  </li>
                </ul>
              </Card>

              <Card className="bg-gray-800/50 border border-purple-500/30 p-6">
                <h4 className="text-lg font-bold text-white mb-3">Choose Pro ($139) If:</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>1-2 locations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Post 15-30 times/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Need advanced scheduling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Want video content</span>
                  </li>
                </ul>
              </Card>

              <Card className="bg-gray-800/50 border border-pink-500/30 p-6">
                <h4 className="text-lg font-bold text-white mb-3">Choose Enterprise If:</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-pink-500 mt-0.5" />
                    <span>3+ locations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-pink-500 mt-0.5" />
                    <span>Post 50+ times/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-pink-500 mt-0.5" />
                    <span>Marketing team</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-pink-500 mt-0.5" />
                    <span>Need automation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-pink-500 mt-0.5" />
                    <span>Want managed service</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>

        </div>
      </Card>
    </div>
  );
}
